import { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Clock, MapPin, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from '../utils/dateUtils';

const localizer = momentLocalizer(moment);

export default function HandymanScheduleCalendar({ handyman }) {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');

  const fetchSchedules = useCallback(async () => {
    if (!handyman?.id) return;
    try {
      const [jobsRes, visitsRes] = await Promise.all([
        axiosInstance.get(`/jobs?handyman_id=${handyman.id}`),
        axiosInstance.get(`/visit-schedules?handyman_id=${handyman.id}`)
      ]);

      const jobEvents = (jobsRes.data || [])
        .filter(job => job.scheduled_date)
        .map(job => ({
          id: job.id,
          title: `${job.service_name} - ${job.customer_name}`,
          start: new Date(job.scheduled_date),
          end: new Date(new Date(job.scheduled_date).getTime() + (job.estimated_hours || 2) * 60 * 60 * 1000),
          resource: { ...job, type: 'job' }
        }));

      const visitEvents = (visitsRes.data || [])
        .filter(visit => visit.scheduled_date)
        .map(visit => ({
          id: visit.id,
          title: `VISIT: ${visit.visit_purpose} - ${visit.customer_name}`,
          start: new Date(visit.scheduled_date),
          end: new Date(new Date(visit.scheduled_date).getTime() + (visit.estimated_duration || 1) * 60 * 60 * 1000),
          resource: { ...visit, type: 'visit' }
        }));

      setEvents([...jobEvents, ...visitEvents]);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [handyman?.id]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const eventStyleGetter = (event) => {
    const r = event.resource;
    let bg = '#6B7280';
    if (r.type === 'visit') {
      bg = r.status === 'completed' ? '#10B981' : r.status === 'cancelled' ? '#EF4444' : '#3B82F6';
    } else {
      const map = { pending: '#FFA500', assigned: '#9333EA', on_the_way: '#6366F1', in_progress: '#F97316', completed: '#10B981' };
      bg = map[r.status] || bg;
    }
    return { style: { backgroundColor: bg, borderRadius: '5px', opacity: 0.9, color: 'white', border: '0px', display: 'block' } };
  };

  const getStatusColor = (status) => {
    const c = { pending: 'bg-yellow-500', assigned: 'bg-purple-500', on_the_way: 'bg-indigo-500', in_progress: 'bg-orange-500', completed: 'bg-green-500', cancelled: 'bg-red-500', scheduled: 'bg-blue-500' };
    return c[status] || 'bg-gray-500';
  };

  const handleNavigate = (action) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + (action === 'next' ? 1 : -1));
    else if (view === 'week') d.setDate(d.getDate() + (action === 'next' ? 7 : -7));
    else d.setDate(d.getDate() + (action === 'next' ? 1 : -1));
    setCurrentDate(d);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div></div>;
  }

  // Get jobs for selected date (for the date-wise list below calendar)
  const selectedDateStr = currentDate.toDateString();
  const dayJobs = events.filter(e => e.start.toDateString() === selectedDateStr)
    .sort((a, b) => a.start - b.start);

  return (
    <div className="space-y-4" data-testid="handyman-schedule-calendar">
      {/* Legend */}
      <Card className="shadow-lg border-0">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-medium text-gray-500 mr-1">Status:</span>
            <Badge className="bg-yellow-500 text-white text-xs">Pending</Badge>
            <Badge className="bg-purple-500 text-white text-xs">Assigned</Badge>
            <Badge className="bg-orange-500 text-white text-xs">In Progress</Badge>
            <Badge className="bg-green-500 text-white text-xs">Completed</Badge>
            <Badge className="bg-blue-500 text-white text-xs">Visit</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card className="shadow-lg border-0">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">My Schedule</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => handleNavigate('prev')} data-testid="cal-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCurrentDate(new Date())} data-testid="cal-today">
                Today
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleNavigate('next')} data-testid="cal-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="flex gap-1 ml-2">
                {['month', 'week', 'day'].map(v => (
                  <Button key={v} size="sm" variant={view === v ? 'default' : 'outline'} onClick={() => setView(v)} className="text-xs capitalize">
                    {v}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 500 }}
            onSelectEvent={(e) => setSelectedEvent(e.resource)}
            onNavigate={(date) => setCurrentDate(date)}
            date={currentDate}
            view={view}
            onView={setView}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day']}
            toolbar={false}
            popup
          />
        </CardContent>
      </Card>

      {/* Day's Jobs List */}
      <Card className="shadow-lg border-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {moment(currentDate).format('dddd, MMMM D, YYYY')} ({dayJobs.length} {dayJobs.length === 1 ? 'job' : 'jobs'})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dayJobs.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">No jobs scheduled for this date</p>
          ) : (
            <div className="space-y-3">
              {dayJobs.map((event) => {
                const item = event.resource;
                return (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setSelectedEvent(item)}
                    data-testid={`day-job-${event.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${getStatusColor(item.status)} text-white text-xs`}>
                          {item.status?.replace('_', ' ')}
                        </Badge>
                        <span className="font-medium text-sm">
                          {item.type === 'visit' ? item.visit_purpose : item.service_name}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
                        <span>{item.customer_name}</span>
                        <span>{moment(event.start).format('h:mm A')}</span>
                        {(item.customer_address || item.address) && (
                          <span>{(item.customer_address || item.address).substring(0, 40)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEvent?.type === 'visit' ? 'Visit Details' : 'Job Details'}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold">
                  {selectedEvent.type === 'visit' ? selectedEvent.visit_purpose : selectedEvent.service_name}
                </h3>
                <Badge className={`${getStatusColor(selectedEvent.status)} text-white mt-2`}>
                  {selectedEvent.status?.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-teal-600" />
                  <span>{selectedEvent.customer_name}</span>
                </div>
                {selectedEvent.customer_phone && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Phone:</span>
                    <span>{selectedEvent.customer_phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-teal-600" />
                  <span>{selectedEvent.customer_address || selectedEvent.address || 'No address'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-teal-600" />
                  <span>{formatDateTime(selectedEvent.scheduled_date)}</span>
                </div>
                {selectedEvent.estimated_hours && (
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-400" />
                    <span>{selectedEvent.estimated_hours}h estimated</span>
                  </div>
                )}
              </div>
              {selectedEvent.notes && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Notes: {selectedEvent.notes}</p>
                </div>
              )}
              <Button variant="outline" className="w-full" onClick={() => setSelectedEvent(null)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
